import { styled } from '@mui/system';
import { Link } from 'react-router-dom';
import { useViewMode } from '../../../hooks/use-view-mode';

interface SidebarItemProps {
  children: React.ReactNode;
  to: string | null;
  title?: string;
  replace?: boolean;
  isActive?: boolean;
  onClick?: (event: React.MouseEvent<HTMLElement, MouseEvent>) => void;
  hasNotification?: boolean;
}

const LinkStyled = styled(Link)<{ short?: boolean; medium?: boolean; small?: boolean }>(
  ({ short, medium, small }) => ({
    position: 'relative',
    marginTop: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '48px',
    width: '48px',
    borderRadius: 14,
    transition: 'all 0.1s ease-in-out',
    '& svg': {
      width: '20px',
      height: '20px',
      opacity: 0.7,
      ...(short && {
        width: '12px',
        height: '12px'
      }),
      ...(medium && {
        width: '15px',
        height: '15px'
      })
    },
    '& svg.CampainButton': {
      width: '36px',
      height: '36px',
      marginLeft: '-8px',
      marginRight: '-8px',
      opacity: 0.7,
      ...(short && {
        width: '20px',
        height: '20px',
        marginLeft: '-4px',
        marginRight: '-4px'
      }),
      ...(medium && {
        width: '26px',
        height: '26px',
        marginLeft: '-6px',
        marginRight: '-6px'
      })
    },
    '& svg.TeamsButton': {
      width: '26px',
      height: '26px',
      opacity: 0.7,
      ...(short && {
        width: '16px',
        height: '16px'
      }),
      ...(medium && {
        width: '20px',
        height: '20px'
      })
    },
    '&:hover': {
      backgroundColor: '#265D7C'
    },
    '&:focus-visible': {
      boxShadow: '0px 0px 0px 3px #1EB9FB',
      outline: 'none'
    },
    ...(short && {
      marginTop: '3px',
      width: '25px',
      height: '25px',
      padding: '2px',
      borderRadius: '8px'
    }),
    ...(medium && {
      marginTop: '5px',
      width: '30px',
      height: '30px',
      padding: '5px',
      borderRadius: '10px'
    }),
    ...(small && {
      width: 'auto',
      height: 'auto',
      padding: '18px',
      borderRadius: '8px'
    })
  })
);

const BlueDot = styled('span')({
  position: 'absolute',
  top: '-3px',
  right: '-3px',
  borderRadius: '100%',
  backgroundColor: '#0675DD',
  display: 'block',
  border: '3px solid #004763',
  width: '8px',
  height: '8px'
});

const Title = styled('span')<{ small?: boolean }>(({ small }) => ({
  display: 'none',
  ...(small && {
    marginLeft: '16px',
    display: 'block',
    flex: 1,
    fontSize: 16,
    fontFamily: 'Roboto',
    fontWeight: 400
  })
}));

const SidebarItem = ({ children, title, to, replace, isActive, onClick, hasNotification }: SidebarItemProps) => {
  const { viewMode, heightMode } = useViewMode();
  const isSmall = viewMode === 'Phone';
  const isShort = heightMode === 'Short';
  const isMedium = heightMode === 'Medium';

  return (
    <LinkStyled
      to={to || ''}
      medium={isMedium ? isMedium : undefined}
      short={isShort ? isShort : undefined}
      small={isSmall ? isSmall : undefined}
      replace={replace}
      onClick={onClick}
      title={title}
      sx={{
        ...(isActive && {
          background: '#003E57',
          '& svg': {
            opacity: 1
          }
        })
      }}
    >
      {children}
      {hasNotification && <BlueDot />}
      <Title small={isSmall ? isSmall : undefined}>{title}</Title>
    </LinkStyled>
  );
};

export default SidebarItem;
